/*
 * Make Your Own Case — instant preview.
 *
 * The storefront half of MYOC-INSTANT-PREVIEW-BRIEF.md. Nothing is rendered
 * here: the page POSTs the customer's link to the preview API (Render,
 * preview_service.py), polls it, and shows the case in a STAGE of its own.
 * Recolouring is the existing colour configurator's job — the API returns
 * the same light/dark plates a premade product carries in its metafield,
 * colour-configurator.js accepts them at runtime through window.ccSetArt(),
 * and its blit() paints into every [data-cc-overlay] on the page.
 *
 * TWO HOMES, BY BREAKPOINT. On a phone (under 768px) the case lives in the
 * stage described below. On desktop the gallery sits beside the form and
 * browsing the product photos next to the preview is worth keeping, so
 * there the case is painted into an overlay in the FIRST SLIDE of the
 * desktop carousel -- the way it always was -- and this script re-creates
 * that overlay whenever the ride tabs or a Case Type change throw the slide
 * away, and moves the carousel back to it. ensureDesktopOverlay() below.
 *
 * THE STAGE (phones). The case used to be painted into an overlay
 * inside the first carousel slide. Three things throw that slide away: the
 * ride tabs (myoc-ride.js detaches slides by ride), a Case Type change (the
 * theme replaces [data-product-media-container]) and embla re-scrolling to
 * the variant's own image. So the preview lives in <div data-myoc-stage>,
 * inserted by this script as the first child of the media column — the
 * parent of the two media containers, never inside them. When a preview is
 * ready the stage shows and the column gets .myoc-preview-on, which hides
 * the containers; "See product photos" swaps back without losing anything.
 * The gallery's own slides stay untouched original images: any overlay the
 * gallery snippets render into them is removed here, on init and after
 * every remount, so the stage's is the only one.
 *
 * Rules that shaped this file:
 *   - Nothing here ever writes into the Website Link field. A suggestion
 *     tap re-runs with that URL and leaves the field alone.
 *   - Emptying the field (typing, or the ✕) ends the preview: colour reset,
 *     plates dropped, stage hidden, gallery back, progress and result gone.
 *   - <variant-picker> re-renders every [data-product-block] on a size
 *     change and fires `tcustomizer-remount`; the panel is rebuilt then.
 *     State lives here, render() is re-entrant, ensureStage() is idempotent.
 *   - One picture, never two: the server's mockup.png is only a fallback
 *     while there is no canvas, and goes the moment one paints.
 *
 * Never: call the API on a keystroke, show a preview for a 400, put the
 * preview URL anywhere near the cart, or translate copy in here (strings
 * come from locales/*.json via a JSON block in product-cs-options.liquid).
 */
(function () {
  'use strict';

  var POLL_MS = 1500;
  var GIVE_UP_MS = 60000;
  var CLICK_DEBOUNCE_MS = 1000;
  var DEFAULT_HEX = '#F2F2F2';          // the server's own default background
  var THUMB_PX = 96;
  var STAGE_ON_CLASS = 'myoc-preview-on';
  var LS_KEY = 'myoc:last';
  var LS_MAX_AGE = 14 * 24 * 60 * 60 * 1000;   // 14 days

  // Progress copy. The API's `stage` key drives it when present; a deploy
  // without it still moves, on the clock, through the same four segments.
  var STAGES = ['reading_page', 'loading_photos', 'finding_the_ride', 'printing_the_case'];
  var STAGE_AT_MS = [0, 2500, 8000, 13000];

  var state = {
    url: '',            // the link the current preview is for
    status: 'idle',     // idle | running | done | failed
    ready: false,       // done AND the case is on screen
    stage: null,
    ride: null,         // 'ski' | 'board' once the API has looked at the page
    t0: 0,
    result: null,       // done payload
    failure: null,      // failed payload / synthetic {message, reason}
    linkMatch: null,    // {url, shop} from POST /preview/match {url}: the catalogue lookup on the link alone
    wantPreview: false, // exact match: the rough preview stays hidden until "Not this board? See a preview of yours"
    showGallery: false, // "See product photos" pressed while a preview is ready
    lastClick: 0,
    restored: false,   // this result came back from localStorage, not the API
    pollTimer: 0,
    paintWait: 0
  };

  /* --------------------------------------------------------------- dom -- */

  function panel() { return document.querySelector('[data-myoc-preview]'); }
  function linkField() { return document.querySelector('[data-myoc-link-field]'); }
  function colourField() { return document.querySelector('[data-myoc-colour-field]'); }
  function progressBox() { return document.querySelector('[data-myoc-preview-progress]'); }
  function clearButton() { return document.querySelector('[data-myoc-clear]'); }
  function stageEl() { return document.querySelector('[data-myoc-stage]'); }
  function colourLockNote() { return document.querySelector('[data-myoc-colour-lock]'); }
  function noteField() { return document.querySelector('[data-myoc-note-field]'); }
  function noteMirror() { return document.querySelector('[data-myoc-note-mirror]'); }

  /*
   * THE DESIGN NOTE HAS TO OUTLIVE THE VARIANT RE-RENDER.
   *
   * A Case Type or Size change makes <variant-picker> fetch the section again
   * and replace every [data-product-block]; everything the customer typed
   * goes with it. The theme puts values back afterwards, but only for
   * [js-product-cs-field] elements and only BY NAME -- and this one carries
   * no name until it has something in it, so there is nothing for that
   * mechanism to match and the note came back blank. Reported from the shop
   * on 12 Sept 2026, in German, with the text typed and then lost.
   *
   * Giving it a permanent name would fix the restore and break the other
   * half of the promise: every order would then carry an empty Design Note.
   * So the value is kept here instead, and put back after the swap.
   */
  var noteValue = '';

  // Kept next to the saved preview, and for the same reason: a customer who
  // wanders off to read the FAQ and comes back should not have to type it
  // again. Same 14 days as the preview.
  var NOTE_KEY = 'myoc:note';

  function loadNote() {
    try {
      var raw = window.localStorage.getItem(NOTE_KEY);
      if (!raw) return '';
      var o = JSON.parse(raw);
      if (!o || !o.text) return '';
      if (Date.now() - (o.ts || 0) > LS_MAX_AGE) { saveNote(''); return ''; }
      return String(o.text);
    } catch (e) { return ''; }
  }

  function saveNote(text) {
    try {
      if (text) window.localStorage.setItem(NOTE_KEY, JSON.stringify({ text: text, ts: Date.now() }));
      else window.localStorage.removeItem(NOTE_KEY);
    } catch (e) {}
  }

  // The textarea is a UI control with no name; this is what actually gets
  // posted. Named only while there is something to say.
  function writeNote(text) {
    noteValue = text;
    var m = noteMirror();
    if (!m) return;
    m.value = text;
    syncPropertyName(m);
  }

  function restoreNote() {
    var el = noteField();
    // Only into an empty box: never overwrite something just typed.
    if (el && noteValue && !el.value) {
      el.value = noteValue;
      var d = el.closest('details');
      if (d) d.open = true;     // or the text is restored where nobody can see it
    }
    // The mirror is re-rendered with the form on a variant change, so it has
    // to be refilled every time, whether or not the textarea needed it.
    writeNote(el ? el.value : noteValue);
  }

  /*
   * NO EMPTY PROPERTIES ON AN ORDER.
   *
   * The theme posts new FormData(form), which includes every NAMED control in
   * the form whether or not it holds anything -- so a blank design note and an
   * unchosen colour would both arrive as properties[...]="" on every order,
   * and the cart line, the packing slip and the fulfilment brief all have to
   * carry them. Shopify does drop empty properties itself, but that puts the
   * promise in someone else's code.
   *
   * The name lives in data-property-name and goes on the element only while
   * there is a value. Idempotent, so it is safe on every keystroke and after
   * every remount.
   */
  function syncPropertyName(el) {
    if (!el) return;
    var wanted = el.getAttribute('data-property-name');
    if (!wanted) return;
    if (el.value.trim()) {
      var name = 'properties[' + wanted + ']';
      if (el.getAttribute('name') !== name) el.setAttribute('name', name);
    } else if (el.hasAttribute('name')) {
      el.removeAttribute('name');
    }
  }

  function syncOptionalProperties() {
    restoreNote();
    syncPropertyName(colourField());
  }

  // Whatever was typed last visit, back in the box.
  function primeNote() {
    var saved = loadNote();
    if (!saved) return;
    noteValue = saved;
    var el = noteField();
    if (el && !el.value) {
      el.value = saved;
      var d = el.closest('details');
      if (d) d.open = true;
    }
    writeNote(saved);
  }

  /*
   * THE SWATCHES ARE INERT UNTIL THERE IS A LINK.
   *
   * Colour is the fun part and the link is the necessary part, so people were
   * picking colours and leaving without pasting anything. The row stays
   * VISIBLE -- someone who wants a black case needs to see black exists --
   * but it cannot be tapped, and a line above it says why.
   *
   * The gate is the SHAPE of the link, not a successful preview: we cannot
   * read a third of the shops people paste (bot walls, eBay, Google share
   * links) and those customers must still get to choose a colour.
   */
  function syncColourLock() {
    var f = linkField();
    var open = !!(f && looksLikeUrl(f.value));
    var root = document.querySelector('[data-cc]');
    if (!root) return;
    root.classList.toggle('cc--locked', !open);
    // The swatches are <button>s. Dimming them and switching off
    // pointer-events stops the mouse and the tap but leaves every one of them
    // in the tab order, where Enter would fire straight past the gate, so the
    // lock has to be the real attribute as well as the look. Re-applied on
    // remount, because a Case Type change rebuilds these.
    var btns = root.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) btns[i].disabled = !open;
    var note = colourLockNote();
    if (note) note.hidden = open;
  }
  function stageOverlay() { var s = stageEl(); return s ? s.querySelector('[data-cc-overlay]') : null; }

  function isDesktop() {
    return !!(window.matchMedia && window.matchMedia('(min-width: 768px)').matches);
  }

  // The desktop gallery's main carousel and its first ATTACHED slide (the ride
  // tabs detach the other rides' slides, so "first" moves with the tab).
  function desktopMedia() {
    var list = document.querySelectorAll('[data-product-media-container]');
    for (var i = 0; i < list.length; i++) {
      if (list[i].classList.contains('product-desktop-media')) return list[i];
    }
    return null;
  }
  function desktopFirstSlide() {
    var dm = desktopMedia();
    if (!dm) return null;
    var slides = dm.querySelectorAll('.product-media-carousel__slide[data-media-id], .product-media-grid__item[data-media-id]');
    for (var i = 0; i < slides.length; i++) if (document.body.contains(slides[i])) return slides[i];
    return null;
  }
  function desktopOverlay() {
    var s = desktopFirstSlide();
    return s ? s.querySelector(':scope > [data-cc-overlay]') : null;
  }

  // Desktop: one overlay, in the first slide of the desktop carousel, built
  // like the one the snippets used to render. Any other slide overlay in
  // that container goes. Returns true when it had to be (re)created.
  function ensureDesktopOverlay() {
    var dm = desktopMedia();
    var slide = desktopFirstSlide();
    if (!dm || !slide) return false;
    var created = false;
    var existing = dm.querySelectorAll('[data-cc-overlay]');
    for (var i = 0; i < existing.length; i++) {
      if (existing[i].parentNode !== slide) existing[i].parentNode.removeChild(existing[i]);
    }
    var ov = slide.querySelector(':scope > [data-cc-overlay]');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'cc-overlay';
      ov.setAttribute('data-cc-overlay', '');
      ov.hidden = true;
      var tag = document.createElement('span');
      tag.className = 'cc-overlay__tag';
      tag.setAttribute('data-cc-tag', '');
      tag.hidden = true;
      ov.appendChild(tag);
      slide.insertBefore(ov, slide.firstChild);
      created = true;
    }
    return created;
  }

  // After a Case Type change the theme jumps the desktop carousel to the
  // variant's own image (VariantPicker.setFirstMediaByVariant -> embla), a
  // beat after the remount event. Bring it back to the slide with the case.
  var slideTimers = [];
  function showDesktopPreviewSlide() {
    var slide = desktopFirstSlide();
    var carousel = slide && slide.closest('[data-product-media-carousel-component]');
    var embla = carousel && carousel.embla;
    if (!slide || !embla || typeof embla.slideNodes !== 'function' || typeof embla.scrollTo !== 'function') return;
    var idx = embla.slideNodes().indexOf(slide);
    if (idx >= 0) { try { embla.scrollTo(idx, true); } catch (e) {} }
  }
  function showDesktopPreviewSlideSoon() {
    for (var i = 0; i < slideTimers.length; i++) clearTimeout(slideTimers[i]);
    slideTimers = [120, 450, 1000].map(function (ms) {
      return setTimeout(function () { if (state.status === 'done' && state.ready) showDesktopPreviewSlide(); }, ms);
    });
  }

  // Shopify's `t` filter HTML-escapes every key that does not end in _html,
  // so "Couldn't" arrives as "Couldn&#39;t" inside the JSON. Decoded once.
  var stringsCache = null;
  function strings() {
    if (stringsCache) return stringsCache;
    var el = document.querySelector('[data-myoc-preview-strings]');
    if (!el) return {};
    var raw;
    try { raw = JSON.parse(el.textContent); } catch (e) { return {}; }
    var ta = document.createElement('textarea');
    var out = {};
    for (var k in raw) {
      if (!Object.prototype.hasOwnProperty.call(raw, k)) continue;
      ta.innerHTML = String(raw[k]);
      out[k] = ta.value;
    }
    stringsCache = out;
    return out;
  }

  function endpoint() {
    var p = panel();
    var base = (p && p.getAttribute('data-endpoint')) || '';
    return base.replace(/\/+$/, '');
  }

  function paintedCanvas() {
    var homes = [stageOverlay(), desktopOverlay()];
    for (var i = 0; i < homes.length; i++) {
      var cv = homes[i] && homes[i].querySelector('canvas');
      if (cv && cv.width > 0) return cv;
    }
    return null;
  }

  /* -------------------------------------------------------------- stage -- */

  // The media column: the parent of the [data-product-media-container]s.
  function mediaColumn() {
    var mc = document.querySelector('[data-product-media-container]');
    return mc ? mc.parentNode : null;
  }

  /*
   * "PREVIEW ONLY - NOT THE FINAL CASE".
   *
   * In normal flow, never over the picture: the case is the thing the
   * customer is judging, and a line meant to be READ has to be readable --
   * full weight, full opacity, not a texture in the margin. Not aria-hidden,
   * unlike the zoom icon: this is the one piece of the preview furniture a
   * screen reader should reach.
   *
   * 'under' sits in the stage, below the case (phones). 'over' sits at the
   * top of the desktop gallery -- below the case there is the "Your colour -
   * Glacier white" pill, and the plate leaves only ~4.5% of empty canvas
   * above the case (about 25px at a 560px box), too little to overlay a line
   * without landing on the case.
   */
  function buildMark(where) {
    var mark = document.createElement('p');
    mark.className = 'myoc-mark myoc-mark--' + where;
    mark.setAttribute('data-myoc-mark', where);
    mark.textContent = strings().preview_watermark || 'PREVIEW ONLY \u2014 NOT THE FINAL CASE';
    return mark;
  }

  function deskMark() { return document.querySelector('[data-myoc-mark="over"]'); }

  /*
   * INSIDE the gallery, not beside it.
   *
   * Not in mediaColumn(): that is .product__content--with-not-fixed-content,
   * the two-column grid itself, and a visible child of it is another grid
   * item -- it shunts the media and the form along a cell and the two columns
   * swap sides. (The stage lives there safely only because it is display:none
   * on desktop, and a display:none element is not a grid item.)
   *
   * .product-desktop-media is display:block and position:sticky, so a first
   * child stacks above the carousel and travels with it on scroll.
   */
  function ensureDeskMark() {
    var dm = desktopMedia();
    if (!dm) return;
    var m = deskMark();
    if (!m || !document.body.contains(m)) {
      m = buildMark('over');
      m.hidden = true;
    }
    if (m.parentNode !== dm || dm.firstElementChild !== m) dm.insertBefore(m, dm.firstChild);
  }

  function buildStage() {
    var s = strings();
    var st = document.createElement('div');
    st.className = 'myoc-stage';
    st.setAttribute('data-myoc-stage', '');
    st.hidden = true;
    var ov = document.createElement('div');
    ov.className = 'cc-overlay';
    ov.setAttribute('data-cc-overlay', '');
    ov.hidden = true;
    var tag = document.createElement('span');
    tag.className = 'cc-overlay__tag';
    tag.setAttribute('data-cc-tag', '');
    tag.hidden = true;
    ov.appendChild(tag);
    st.appendChild(ov);
    // Zoom affordance: the icon in the corner, and the overlay itself is a
    // zoom-in target (see the click handler below).
    var zoom = document.createElement('button');
    zoom.type = 'button';
    zoom.className = 'myoc-stage__zoom';
    zoom.setAttribute('data-myoc-zoom', '');
    zoom.setAttribute('aria-label', s.preview_thumb_label || 'Zoom');
    zoom.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';
    ov.appendChild(zoom);

    /*
     * "PREVIEW ONLY - NOT THE FINAL CASE", once, under the case.
     *
     * In normal flow rather than over the picture: the case is the thing the
     * customer is judging and nothing should sit on it, and a line that is
     * meant to be READ has to be readable -- full weight, full opacity, not a
     * texture in the margin. It is not aria-hidden, unlike the zoom icon: it
     * is the one piece of this stage a screen reader should reach.
     *
     * No visibility logic of its own. It is a child of the stage, and the
     * stage is shown only while a preview is (syncStage), so switching to
     * the gallery or clearing with the X takes the line with it.
     */
    st.appendChild(buildMark('under'));
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'myoc-stage__photos';
    btn.setAttribute('data-myoc-see-photos', '');
    btn.textContent = s.preview_see_photos || 'See product photos';
    st.appendChild(btn);
    return st;
  }

  // Idempotent. Creates the stage once, keeps it as the column's first child,
  // and strips the slide overlays the gallery snippets render, so the stage's
  // is the only [data-cc-overlay] the configurator paints. Returns true when
  // the stage had to be (re)inserted -- the caller repaints then.
  function ensureStage() {
    var col = mediaColumn();
    if (!col) return false;
    // the mobile gallery never carries an overlay (the stage is its home);
    // the desktop gallery keeps exactly one, managed by ensureDesktopOverlay()
    var slideOverlays = col.querySelectorAll('[data-product-media-container]:not(.product-desktop-media) [data-cc-overlay]');
    for (var i = 0; i < slideOverlays.length; i++) slideOverlays[i].parentNode.removeChild(slideOverlays[i]);

    var st = stageEl();
    var inserted = false;
    if (!st || !document.body.contains(st)) {
      st = buildStage();
      inserted = true;
    }
    if (st.parentNode !== col || col.firstElementChild !== st) {
      col.insertBefore(st, col.firstChild);
    }
    var created = ensureDesktopOverlay();
    ensureDeskMark();
    syncStage();
    return inserted || created;
  }

  // Stage vs gallery, from state: preview ready and not switched away -> stage.
  function syncStage() {
    var st = stageEl();
    var col = mediaColumn();
    var hiddenByCard = state.status === 'done' && exactOnScreen(state.result);
    var on = state.status === 'done' && state.ready && !state.showGallery && !isDesktop() && !hiddenByCard;
    if (st) st.hidden = !on;
    if (col) col.classList.toggle(STAGE_ON_CLASS, on);
    // `on` is false on desktop -- the stage is a phone home -- so the desktop
    // line carries its own gate. Neither can appear at the other's
    // breakpoint: CSS hides each outside its own.
    var dm = deskMark();
    if (dm) dm.hidden = !(state.status === 'done' && state.ready && isDesktop() && !hiddenByCard);
  }

  function showStage() {
    state.showGallery = false;
    syncStage();
  }

  function showGallery() {
    state.showGallery = true;
    syncStage();
  }

  function scrollToStage() {
    var st = stageEl();
    if (!st || st.hidden) return;
    var h = document.querySelector('[data-header-component], .header, header');
    var off = 12;
    if (h) {
      var pos = getComputedStyle(h).position;
      if (pos === 'sticky' || pos === 'fixed') off = h.getBoundingClientRect().height + 12;
    }
    var top = st.getBoundingClientRect().top + window.pageYOffset - off;
    try { window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, Math.max(0, top)); }
  }

  /* ------------------------------------------------------------ memory -- */

  /*
   * THE LAST PREVIEW SURVIVES A RELOAD.
   *
   * Building one costs 15-40 seconds of somebody's attention, and a phone
   * loses the page for all the ordinary reasons -- a call, a tab evicted, a
   * share link opened and backed out of. Coming back to an empty form reads
   * as "it did not work".
   *
   * Every access is wrapped: Safari in private mode throws on localStorage
   * rather than returning null, and a throw here would take the whole script
   * down on a page where the preview is the point.
   */
  function lsGet() {
    try {
      var raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.url || !v.urls || !v.ts) return null;
      if (Date.now() - v.ts > LS_MAX_AGE) return null;
      return v;
    } catch (e) { return null; }
  }

  function lsSet() {
    if (state.status !== 'done' || !state.result) return;
    var r = state.result;
    var urls = r.urls || {};
    var cc = (window.ccState && window.ccState()) || {};
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify({
        url: state.url,
        key: r.key || r.id || null,
        urls: { mockup: urls.mockup || null, light: urls.light || null, dark: urls.dark || null },
        brand: r.brand || null,
        model: r.model || null,
        ride: state.ride || r.ride || null,
        colour: cc.hex || null,
        // Not in the brief's shape, but without it a restored case comes back
        // as "#1B4F9C" in the cart instead of "Alpine blue (#1B4F9C)" -- the
        // name only lives in the configurator, and ccChoose(hex, null) drops
        // it. Readers that do not know the field simply ignore it.
        colourName: cc.name || null,
        ts: Date.now()
      }));
    } catch (e) {}
  }

  function lsClear() {
    try { window.localStorage.removeItem(LS_KEY); } catch (e) {}
  }

  /*
   * Put it back, if the customer has not typed anything themselves.
   *
   * The images live on Render's ephemeral disk, so a redeploy between visits
   * wipes them: a saved entry can point at a 404. Showing a broken frame is
   * worse than showing nothing, so the light plate is checked first and a
   * miss quietly re-runs the preview for the same URL -- same progress bar,
   * same result, the customer never learns anything went missing.
   */
  function restoreLast() {
    var f = linkField();
    if (!f || f.value.trim()) return;              // never over what they typed
    var saved = lsGet();
    if (!saved) return;

    // The field first, so the X and the button behave like any other visit.
    f.value = saved.url;
    render();
    syncColourLock();

    var body = {
      status: 'done', brand: saved.brand, model: saved.model,
      ride: saved.ride, urls: saved.urls, key: saved.key
    };

    var replay = function () {
      if (linkField() && linkField().value.trim() !== saved.url) return;
      state.restored = false;
      lsClear();                                   // the new run writes a fresh one
      run(saved.url, true);
    };

    if (!saved.urls || !saved.urls.light) return replay();

    fetch(saved.urls.light, { method: 'HEAD' }).then(function (res) {
      // Anything but a clean 200 is treated as gone. A CORS-opaque or thrown
      // response is indistinguishable from a miss here, and re-running costs
      // one API call where guessing wrong costs a broken picture.
      if (res.status !== 200) return replay();
      var cur = linkField();
      if (!cur || cur.value.trim() !== saved.url) return;   // they typed meanwhile
      state.restored = true;
      state.url = saved.url;
      state.status = 'running';
      state.t0 = Date.now();
      state.ready = false;
      state.failure = null;
      state.showGallery = false;
      state.ride = normalizeRide(saved.ride) || null;
      pendingColour = saved.colour ? { hex: saved.colour, name: saved.colourName || null } : null;
      done(body);
    }).catch(replay);
  }

  // Set only by restoreLast(), consumed once by done(): the colour the
  // customer had last time, which is not in the Custom Color field yet.
  var pendingColour = null;

  /* --------------------------------------------------------------- url -- */

  function looksLikeUrl(v) {
    v = (v || '').trim();
    if (v.length < 8 || /\s/.test(v)) return false;
    return /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(v);
  }

  function normalizeUrl(v) {
    v = (v || '').trim();
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    return v;
  }

  function hexIn(text) {
    var m = /#?([0-9a-f]{6})\b/i.exec(text || '');
    if (m) return '#' + m[1].toUpperCase();
    var m3 = /#([0-9a-f]{3})\b/i.exec(text || '');
    if (m3) {
      var s = m3[1];
      return ('#' + s[0] + s[0] + s[1] + s[1] + s[2] + s[2]).toUpperCase();
    }
    return null;
  }

  function currentHex() {
    if (window.ccState) {
      var st = window.ccState();
      if (st && st.hex) return st.hex;
    }
    var f = colourField();
    return (f && hexIn(f.value)) || null;
  }

  /* ----------------------------------------------------------- progress -- */

  // "ski" | "snowboard" from the API -> 'ski' | 'board' | null
  function normalizeRide(v) {
    v = String(v || '').toLowerCase();
    if (!v) return null;
    if (v.indexOf('board') !== -1) return 'board';
    if (v.indexOf('ski') !== -1) return 'ski';
    return null;
  }

  function stageText(stage) {
    var s = strings();
    var finding = (state.ride && s['stage_finding_' + state.ride]) || s.stage_finding;
    var map = {
      reading_page: s.stage_reading, loading_photos: s.stage_photos,
      finding_the_ride: finding, printing_the_case: s.stage_printing
    };
    return map[stage] || s.preview_busy || 'Building your preview…';
  }

  function stageByClock() {
    var el = Date.now() - state.t0;
    var idx = 0;
    for (var i = 0; i < STAGE_AT_MS.length; i++) if (el >= STAGE_AT_MS[i]) idx = i;
    return STAGES[idx];
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Four segments that fill one per stage, the stage line under them, and
  // the honest expectation. Lives under the Preview button, where the
  // customer is looking; the gallery is left alone while it runs.
  function progressNode(pb) {
    var n = pb.querySelector('[data-myoc-progress]');
    if (!n) {
      n = document.createElement('div');
      n.className = 'myoc-progress';
      n.setAttribute('data-myoc-progress', '');
      n.setAttribute('role', 'status');
      n.setAttribute('aria-live', 'polite');
      n.innerHTML =
        '<span class="myoc-progress__bar" aria-hidden="true"><i></i><i></i><i></i><i></i></span>' +
        '<span class="myoc-progress__text" data-myoc-progress-text></span>' +
        '<span class="myoc-progress__eta">' + escapeHtml(strings().preview_eta || '') + '</span>';
      pb.appendChild(n);
    }
    return n;
  }

  function showProgress() {
    var stage = state.stage || stageByClock();
    var idx = Math.max(0, STAGES.indexOf(stage));
    var pb = progressBox();
    if (!pb) return;
    pb.hidden = false;
    var n = progressNode(pb);
    n.querySelector('[data-myoc-progress-text]').textContent = stageText(stage);
    var segs = n.querySelectorAll('.myoc-progress__bar i');
    for (var d = 0; d < segs.length; d++) segs[d].classList.toggle('is-on', d <= idx);
    n.hidden = false;
  }

  function hideProgress() {
    var pb = progressBox();
    if (pb) { pb.innerHTML = ''; pb.hidden = true; }
  }

  function removeFallback() {
    var list = document.querySelectorAll('[data-myoc-fallback]');
    for (var i = 0; i < list.length; i++) list[i].parentNode.removeChild(list[i]);
  }

  function showFallbackImage(url) {
    var homes = [stageOverlay(), desktopOverlay()];
    for (var h = 0; h < homes.length; h++) {
      var ov = homes[h];
      if (!ov) continue;
      var im = ov.querySelector('[data-myoc-fallback]');
      if (!im) {
        im = document.createElement('img');
        im.className = 'myoc-fallback';
        im.setAttribute('data-myoc-fallback', '');
        im.alt = '';
        ov.appendChild(im);
      }
      im.src = url;
      ov.hidden = false;
    }
  }

  /* -------------------------------------------------------------- thumb -- */

  // The finished case, small, next to the result — on a phone the stage is
  // a long way above the field. Same pixels as the stage canvas; tapping it
  // brings the stage back (if the gallery was showing) and scrolls to it.
  function drawThumb() {
    var th = document.querySelector('[data-myoc-thumb]');
    if (!th) return;
    var src = paintedCanvas();
    var img = th.querySelector('img');
    var cv = th.querySelector('canvas');
    if (src) {
      if (img) img.parentNode.removeChild(img);
      if (!cv) {
        cv = document.createElement('canvas');
        cv.width = THUMB_PX * 2;
        cv.height = THUMB_PX * 2;
        th.appendChild(cv);
      }
      var g = cv.getContext('2d');
      if (!g) return;
      g.clearRect(0, 0, cv.width, cv.height);
      var r = Math.min(cv.width / src.width, cv.height / src.height);
      var w = src.width * r, h = src.height * r;
      g.drawImage(src, (cv.width - w) / 2, (cv.height - h) / 2, w, h);
      th.hidden = false;
    } else if (document.querySelector('[data-myoc-fallback]') && state.result && state.result.urls && state.result.urls.mockup) {
      if (cv) cv.parentNode.removeChild(cv);
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        th.appendChild(img);
      }
      img.src = state.result.urls.mockup;
      th.hidden = false;
    } else {
      th.hidden = true;
    }
  }

  /* -------------------------------------------------------------- panel -- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* -------------------------------------------------------------- shop -- */

  /*
   * THE ONE-LINE STRIP, ABOVE THE PREVIEW.
   *
   * The API looks the brand and model up in the catalogue while it builds
   * the preview and sends shop: {exact, same_model}. When the shop already
   * sells this case -- this year's, or the same model another year -- that
   * is said in a single quiet line above the rough preview, and the whole
   * line is the link. A NOTE, not a banner: many customers come here
   * precisely because they want their own stats or blurb on a board we
   * already sell, and this must not shout at them. Same tab, on purpose:
   * this is the page they should be on.
   *
   * Normal weight throughout except the board name (or the year), which is
   * the one thing the customer is checking. The arrow is the string's last
   * word for translators, but it is rendered as its own element at the far
   * right, so it stays on the last line however the text wraps.
   *
   * shop.similar is never read: on a finished preview it is always empty.
   */
  function shopFor(src) {
    var shop = src && src.shop;
    // No shop on this payload (a failure, or nothing previewed yet): the
    // link-only lookup that ran when the link was pasted stands in, as long
    // as it is for the link that is in the field now.
    var empty = !shop || typeof shop !== 'object' || (!shop.exact && !(Array.isArray(shop.same_model) && shop.same_model.length));
    if (empty && state.linkMatch && state.linkMatch.shop) {
      var f0 = linkField();
      if (f0 && normalizeUrl(f0.value) === state.linkMatch.url) shop = state.linkMatch.shop;
    }
    if (!shop || typeof shop !== 'object') return null;
    var ok = function (it) { return !!(it && typeof it === 'object' && it.url); };
    var same = [];
    var list = Array.isArray(shop.same_model) ? shop.same_model : [];
    for (var i = 0; i < list.length; i++) if (ok(list[i])) same.push(list[i]);
    var opts = [];
    var ol = Array.isArray(shop.options) ? shop.options : [];
    for (var j = 0; j < ol.length; j++) if (ok(ol[j])) opts.push(ol[j]);
    var out = { exact: ok(shop.exact) ? shop.exact : null, options: opts.slice(0, 3), sameModel: same };
    return (out.exact || out.options.length || out.sameModel.length) ? out : null;
  }

  // The year the strip names. The pipeline sends one; when it is blank the
  // title usually carries it ("... 2027 Ski"), and that is used.
  function shopYear(item) {
    if (item.year != null && String(item.year).trim()) return String(item.year).trim();
    var m = /\b(20\d\d)\b/.exec(item.title || '');
    return m ? m[1] : '';
  }

  // "Burton Instigator PurePop Snowboard Phone Case" -> the board's name.
  function shopShortTitle(title) {
    return String(title || '')
      .replace(/\s+(snowboard|ski)\s+phone\s+case\s*$/i, '')
      .replace(/\s+phone\s+case\s*$/i, '')
      .trim();
  }

  function shopStrip(shop, s) {
    if (!shop) return null;
    var item, tier, text, slot, value;
    if (shop.exact) {
      item = shop.exact; tier = 'exact';
      text = String(s.preview_shop_exact_line || '');
      slot = '%TITLE%'; value = shopShortTitle(item.title);
    } else {
      item = shop.sameModel[0]; tier = 'same_model';
      text = String(s.preview_shop_same_model_line || '');
      slot = '%YEAR%'; value = shopYear(item);
    }
    if (!text) return null;
    // the arrow is drawn as its own element at the far right
    text = text.replace(/\s*\u2192\s*$/, '').replace(/\s{2,}/g, ' ').trim();

    var a = el('a', 'myoc-shop-strip');
    a.href = item.url;
    a.setAttribute('data-myoc-shop-strip', tier);
    var check = el('span', 'myoc-shop-strip__check', '\u2713');
    check.setAttribute('aria-hidden', 'true');
    a.appendChild(check);
    var body = el('span', 'myoc-shop-strip__text');
    var cutAt = text.indexOf(slot);
    if (cutAt >= 0 && value) {
      body.appendChild(document.createTextNode(text.slice(0, cutAt)));
      body.appendChild(el('strong', null, value));
      body.appendChild(document.createTextNode(text.slice(cutAt + slot.length)));
    } else {
      body.textContent = text.replace(slot, value || '').replace(/\s{2,}/g, ' ').trim();
    }
    a.appendChild(body);
    var arrow = el('span', 'myoc-shop-strip__arrow', '\u2192');
    arrow.setAttribute('aria-hidden', 'true');
    a.appendChild(arrow);
    return a;
  }

  /*
   * THE CARD, for an EXACT match.
   *
   * A one-line strip was easy to scroll past, and on a failed preview it was
   * not shown at all -- so a customer whose ski we already sell as a finished
   * case left with "it doesn't work". The card is the finished case: picture,
   * name, "from" price in the shopper's own currency, one button. Same tab.
   * "or design your own" stays right under it, because some people are here
   * for their own colour or text on a board we sell. Same-model-other-year
   * keeps the quiet strip.
   */
  var priceCache = {};
  function shopUrl(item) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return item.handle ? root.replace(/\/$/, '') + '/products/' + item.handle : item.url;
  }
  // "from €32.95": the cheapest variant, in the shopper's currency (the
  // market's currency is on the page; Intl formats it the way the shopper's
  // language writes money). Falls back to "32.95 EUR".
  function moneyFmt(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var lang = (document.documentElement.getAttribute('lang') || 'en').split('-')[0];
    try { return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2) + ' ' + cur; }
  }
  function fillPrice(node, item, s) {
    if (!item.handle) return;
    var tpl = String((s && s.shop_card_from) || 'from %PRICE%');
    if (priceCache[item.handle]) { node.textContent = tpl.replace('%PRICE%', priceCache[item.handle]); return; }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root.replace(/\/$/, '') + '/products/' + item.handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var cents = p && (p.price_min != null ? p.price_min : p.price);
        if (cents == null) return;
        priceCache[item.handle] = moneyFmt(cents);
        node.textContent = tpl.replace('%PRICE%', priceCache[item.handle]);
      }).catch(function () {});
  }
  function shopCard(item, s) {
    var wrap = el('div', 'myoc-shop-card-wrap');
    var card = el('a', 'myoc-shop-card');
    card.href = shopUrl(item);
    card.setAttribute('data-myoc-shop-card', 'exact');
    if (item.image) {
      var img = el('img', 'myoc-shop-card__img');
      img.src = item.image; img.alt = ''; img.loading = 'lazy';
      card.appendChild(img);
    }
    var body = el('div', 'myoc-shop-card__body');
    body.appendChild(el('p', 'myoc-shop-card__eyebrow', '\u2713 ' + (s.shop_card_eyebrow || 'We already make this one')));
    body.appendChild(el('p', 'myoc-shop-card__title', shopShortTitle(item.title)));
    var price = el('p', 'myoc-shop-card__price', '');
    body.appendChild(price);
    fillPrice(price, item, s);
    body.appendChild(el('span', 'myoc-shop-card__btn', (s.shop_card_cta || 'Get this case') + ' \u2192'));
    card.appendChild(body);
    wrap.appendChild(card);
    if (s.shop_card_or) wrap.appendChild(el('p', 'myoc-shop-card__or', s.shop_card_or));
    if (!state.wantPreview) wrap.appendChild(notThisLink(s));
    return wrap;
  }
  // Several of our products share the link's base model and year (Custom
  // Camber / Flying V / X 2026): small rows, and the rough preview below for
  // "none of these".
  function shopOptions(list, s) {
    var wrap = el('div', 'myoc-shop-options');
    wrap.appendChild(el('p', 'myoc-shop-options__title', s.shop_options_title || 'We make these. Is yours one of them?'));
    for (var i = 0; i < Math.min(3, list.length); i++) {
      (function (item) {
        var a = el('a', 'myoc-shop-options__row');
        a.href = shopUrl(item);
        if (item.image) { var im = el('img', 'myoc-shop-options__img'); im.src = item.image; im.alt = ''; a.appendChild(im); }
        var body = el('span', 'myoc-shop-options__body');
        body.appendChild(el('span', 'myoc-shop-options__name', shopShortTitle(item.title)));
        var pr = el('span', 'myoc-shop-options__price', ''); body.appendChild(pr); fillPrice(pr, item, s);
        a.appendChild(body);
        a.appendChild(el('span', 'myoc-shop-options__arrow', '\u2192'));
        wrap.appendChild(a);
      })(list[i]);
    }
    return wrap;
  }
  // card for an exact match; rows when it could be one of several; the
  // quiet strip for the same model another year
  function shopBlock(shop, s) {
    if (!shop) return null;
    if (shop.exact) return shopCard(shop.exact, s);
    if (shop.options && shop.options.length) return shopOptions(shop.options, s);
    return shopStrip(shop, s);
  }
  function exactOnScreen(src) {
    var sh = shopFor(src);
    return !!(sh && sh.exact) && !state.wantPreview;
  }
  // "Not this board? See a preview of yours": shows the preview that was
  // built, or builds it.
  function notThisLink(s) {
    var a = el('a', 'myoc-shop-card__not-this', s.shop_card_not_this || 'Not this board? See a preview of yours');
    a.href = '#';
    a.addEventListener('click', function (e) {
      e.preventDefault();
      state.wantPreview = true;
      if (state.status === 'done' && state.result) { render(); syncStage(); drawThumb(); if (state.ready) { showStage(); scrollToStage(); } }
      else { var f = linkField(); if (f && looksLikeUrl(f.value)) run(f.value, true); else render(); }
    });
    return a;
  }

  // The link-only lookup: fired as soon as a link is pasted, answers in
  // under a second, independent of the preview. Cached per link.
  var linkMatchTimer = 0;
  function matchLink(raw) {
    var url = normalizeUrl(raw);
    if (!url || (state.linkMatch && state.linkMatch.url === url)) return;
    request('POST', '/preview/match', { url: url }).then(function (r) {
      var f = linkField();
      if (!f || normalizeUrl(f.value) !== url) return;          // the field moved on
      state.linkMatch = { url: url, shop: (r.status === 200 && r.body && r.body.shop) ? r.body.shop : null };
      render();
    }).catch(function () {});
  }

  // 'ski' | 'board' | 'generic': the API's ride first, then its brand/model/stats.
  function rideKind(r) {
    var fromApi = normalizeRide(r.ride) || state.ride;
    if (fromApi) return fromApi;
    var blob = [r.brand, r.model].concat(r.stats || []).join(' ').toLowerCase();
    if (/snowboard|skateboard|splitboard|\bboard\b/.test(blob)) return 'board';
    if (/\bskis?\b/.test(blob)) return 'ski';
    return 'generic';
  }

  function render() {
    var p = panel();
    if (!p) return;
    var s = strings();
    var btn = p.querySelector('[data-myoc-preview-btn]');
    var hint = p.querySelector('[data-myoc-preview-hint]');
    var box = p.querySelector('[data-myoc-preview-result]');
    var f = linkField();
    var value = f ? f.value : '';
    var active = looksLikeUrl(value);
    var running = state.status === 'running';

    // Always on the page, dimmed and inert until there is a link to preview.
    if (btn) {
      btn.hidden = false;
      var enabled = active && !running;
      btn.disabled = !enabled;
      btn.classList.toggle('is-inactive', !active);
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      btn.setAttribute('aria-busy', running ? 'true' : 'false');
      var cardUp = !running && exactOnScreen(state.status === 'done' ? state.result : (state.status === 'failed' ? state.failure : null));
      btn.textContent = running ? (s.preview_busy || 'Building your preview…')
                                : cardUp ? (s.preview_button_alt || s.preview_button || 'Preview my case')
                                         : (s.preview_button || 'Preview my case');
    }
    if (hint) hint.hidden = active || running;      // the progress panel takes its place
    var cb = clearButton();
    if (cb) cb.hidden = !value.trim();

    if (!box) return;
    box.innerHTML = '';
    box.hidden = true;
    box.classList.remove('has-thumb');
    box.classList.remove('is-failed');
    box.classList.remove('is-match');

    if (state.status === 'done' && state.result && state.ready) {
      var r = state.result;
      var name = [r.brand, r.model].filter(Boolean).join(' ');
      // The finished case above the preview when the shop already sells it:
      // the card for an exact match, one quiet line for another year.
      var strip = shopBlock(shopFor(r), s);
      if (strip) box.appendChild(strip);
      if (exactOnScreen(r)) {
        // We sell this case: the card is the answer. The rough preview that
        // was built stays behind "Not this board? See a preview of yours".
        box.classList.add('is-match');
        box.hidden = false;
        return;
      }

      var thumb = el('button', 'myoc-preview__thumb');
      thumb.type = 'button';
      thumb.setAttribute('data-myoc-thumb', '');
      thumb.setAttribute('aria-label', s.preview_thumb_label || '');
      thumb.hidden = true;
      thumb.addEventListener('click', function () { showStage(); scrollToStage(); });
      box.appendChild(thumb);
      var kind = rideKind(r);
      var text = el('div', 'myoc-preview__text');
      text.appendChild(el('p', 'myoc-preview__headline', s.preview_headline));
      text.appendChild(el('p', 'myoc-preview__body', s['preview_body_' + kind] || s.preview_body));
      if (name) {
        // The Liquid side cannot pass "{{ name }}" through | t (braces inside
        // a string literal break the parser), so the placeholder is %NAME%.
        var tpl = s['preview_detected_' + kind] || s.preview_detected_generic || '%NAME%';
        text.appendChild(el('p', 'myoc-preview__detected', String(tpl).replace('%NAME%', name)));
      }
      // Why a case is already on screen on a page they have only just
      // opened. Only ever set by restoreLast(); a fresh run clears it.
      if (state.restored && s.preview_restored) {
        text.appendChild(el('p', 'myoc-preview__restored', s.preview_restored));
      }
      box.appendChild(text);
      box.classList.add('has-thumb');
      box.hidden = false;
      drawThumb();
    } else if (state.status === 'failed' && state.failure) {
      var fa = state.failure;

      /*
       * THE FIRST LINE IS ALWAYS THE REASSURANCE.
       *
       * A third of pasted links cannot be previewed -- bot walls, eBay,
       * category pages -- and session replays showed that a failure here
       * read as "this product does not work", and people left. Nothing is
       * wrong with their order: the designer works from the link by hand.
       * So that is said first, every time, before any suggestion, and a
       * button to the order follows the copy. The kind, when the API names
       * one, gets a second line the customer can act on.
       */
      box.classList.add('is-failed');          // the notice styling
      // The catalogue match comes FIRST, failure or not: the preview failing
      // on a bot wall must never hide that we already make their case.
      var fshopObj = shopFor(fa);
      var fshop = shopBlock(fshopObj, s);
      if (fshop) box.appendChild(fshop);
      var exactShown = !!(fshopObj && fshopObj.exact);
      box.appendChild(el('p', 'myoc-preview__reassure', s.preview_fail_reassure));
      var explained = false;                     // a kind line says WHY
      if (fa.kind === 'blocked' && s.preview_fail_blocked) {
        box.appendChild(el('p', 'myoc-preview__kind', s.preview_fail_blocked));
        explained = true;
      } else if (fa.kind === 'notproduct' && s.preview_fail_notproduct) {
        box.appendChild(el('p', 'myoc-preview__kind', s.preview_fail_notproduct));
        explained = true;
      }

      // preview_error says the same thing as the reassurance; with that
      // line now leading, printing it again would just repeat it.
      var msg = fa.message && fa.message !== s.preview_error ? fa.message : null;

      // The fallbacks -- "paste from another shop", the search link -- come
      // last, small: they are the secondary path, most people should just
      // scroll on and order. (There is no "Continue to order" button: Add to
      // cart is a short way down this same column.)
      var after = [];

      if (exactShown) {
        // The card above answers "is it one of these?" — the evo
        // suggestions and the other-shop line would only pull away from it.
      } else if (fa.suggestions && fa.suggestions.length) {
        // Fix-it options first: a tap re-runs the preview.
        box.appendChild(el('p', 'myoc-preview__msg', s.preview_fail_suggest));
        var ul = el('div', 'myoc-preview__suggest');
        for (var i = 0; i < Math.min(3, fa.suggestions.length); i++) {
          (function (sug) {
            var b = el('button', 'myoc-preview__row', sug.title || sug.url);
            b.type = 'button';
            // Re-runs with the suggestion. The customer's own link stays in
            // the field: the designer still works from what they pasted.
            b.addEventListener('click', function () { run(sug.url, true); });
            ul.appendChild(b);
          })(fa.suggestions[i]);
        }
        box.appendChild(ul);
        after.push(el('p', 'myoc-preview__alt', s.preview_other_shop));
      } else if (fa.search_url) {
        // "Couldn't read that page." only when nothing above has said why.
        if (!explained) box.appendChild(el('p', 'myoc-preview__msg', s.preview_fail_short));
        after.push(el('p', 'myoc-preview__alt', s.preview_other_shop));
        var a = el('a', 'myoc-preview__link', s.preview_fail_search_link || 'evo.com');
        a.href = fa.search_url;
        a.target = '_blank';
        a.rel = 'noopener';
        after.push(a);
      } else if (msg) {
        box.appendChild(el('p', 'myoc-preview__msg', msg));   // the server's own words
      } else {
        // preview_fail_plain used to carry the reassurance; that is the
        // first line now, so only the alternative is left to say.
        after.push(el('p', 'myoc-preview__alt', s.preview_other_shop));
      }

      for (var k = 0; k < after.length; k++) box.appendChild(after[k]);
      box.hidden = false;
    } else if (state.status === 'idle' && active) {
      // A link is in the field and nothing has been previewed yet: the
      // link-only lookup may already know we sell this case.
      var early = shopBlock(shopFor(null), s);
      if (early) {
        box.classList.add('is-match');
        box.appendChild(early);
        box.hidden = false;
      }
    }
  }

  /* ---------------------------------------------------------------- api -- */

  function request(method, path, body) {
    var opts = { method: method, headers: { 'Accept': 'application/json' } };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(endpoint() + path, opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        return { status: res.status, body: json || {} };
      });
    });
  }

  function stopPolling() {
    if (state.pollTimer) { clearTimeout(state.pollTimer); state.pollTimer = 0; }
    if (state.paintWait) { clearInterval(state.paintWait); state.paintWait = 0; }
  }

  // Field emptied: everything back to the start. The colour goes too --
  // ccReset() deselects the swatch and clears the Custom Color field if the
  // text there is the one the picker wrote (it tracks that itself).
  function resetAll() {
    stopPolling();
    lsClear();                    // the X means "not this one", next visit too
    state.restored = false;
    state.url = '';
    state.status = 'idle';
    state.ready = false;
    state.stage = null;
    state.ride = null;
    state.result = null;
    state.failure = null;
    state.showGallery = false;
    if (window.ccReset) window.ccReset();
    if (window.ccClearArt) window.ccClearArt();
    removeFallback();
    var ov = stageOverlay();
    if (ov) ov.hidden = true;
    syncStage();
    hideProgress();
    render();
  }

  function run(url, fromSuggestion) {
    var now = Date.now();
    if (state.status === 'running') return;
    if (!fromSuggestion && now - state.lastClick < CLICK_DEBOUNCE_MS) return;
    state.lastClick = now;

    url = normalizeUrl(url);
    stopPolling();
    state.wantPreview = true;        // Preview was asked for: show it even when we sell the case
    state.url = url;
    state.status = 'running';
    state.ready = false;
    state.stage = null;
    state.ride = null;
    state.t0 = now;
    state.result = null;
    state.failure = null;
    state.showGallery = false;
    state.restored = false;       // this is a fresh run, not last week's
    render();
    showProgress();

    var body = { url: url };
    var hex = currentHex();
    if (hex) body.colour = hex;

    request('POST', '/preview', body).then(function (r) {
      if (state.url !== url) return;                 // superseded or reset
      if (r.status === 200 && r.body.status === 'done') return done(r.body);
      if (r.status === 202 && r.body.id) return poll(r.body.id, url);
      // A failure answered on the POST itself, not through a poll: the body
      // carries the same kind / suggestions / search_url as a polled one, and
      // the failure copy keys off them, so they come along.
      fail({ message: r.body.error || strings().preview_error, reason: 'http ' + r.status,
             kind: r.body.kind, suggestions: r.body.suggestions, search_url: r.body.search_url,
             shop: r.body.shop });
    }).catch(function () {
      if (state.url === url) fail({ message: strings().preview_error, reason: 'network' });
    });
  }

  function poll(id, url) {
    if (state.url !== url || state.status !== 'running') return;
    if (Date.now() - state.t0 > GIVE_UP_MS) {
      return fail({ message: strings().preview_timeout, reason: 'timeout' });
    }
    request('GET', '/preview/' + encodeURIComponent(id)).then(function (r) {
      if (state.url !== url || state.status !== 'running') return;
      var b = r.body || {};
      if (b.status === 'done') return done(b);
      if (b.status === 'failed') return fail(b);
      if (r.status >= 400) return fail({ message: b.error || strings().preview_error, reason: 'http ' + r.status });
      state.stage = (b.stage && STAGES.indexOf(b.stage) !== -1) ? b.stage : null;
      if (b.ride) state.ride = normalizeRide(b.ride) || state.ride;
      showProgress();
      state.pollTimer = setTimeout(function () { poll(id, url); }, POLL_MS);
    }).catch(function () {
      state.pollTimer = setTimeout(function () { poll(id, url); }, POLL_MS);
    });
  }

  function done(body) {
    stopPolling();
    state.status = 'done';
    state.ready = false;                             // result shows once the case is on screen
    state.result = body;
    if (body.ride) state.ride = normalizeRide(body.ride) || state.ride;
    state.stage = 'printing_the_case';
    showProgress();                                  // 4/4, until the case is painted
    render();
    ensureStage();

    var urls = body.urls || {};
    if (window.ccSetArt && (urls.light || urls.dark)) {
      window.ccSetArt({ light: urls.light, dark: urls.dark });
      var hex = currentHex();
      var saved = pendingColour;
      pendingColour = null;
      if (window.ccChoose) {
        // Silent: none of these is a tap on a swatch. The property is still
        // written -- the configurator's MYOC default has already set
        // userPicked -- so a restored case reaches the cart with its colour.
        if (saved && saved.hex) window.ccChoose(saved.hex, saved.name, { silent: true });
        else if (hex) window.ccChoose(hex, null, { silent: true });
        else window.ccChoose(DEFAULT_HEX, strings().preview_default_colour || null, { silent: true });
      }

      /*
       * A preview that has just finished builds itself onto the case.
       *
       * Only this path. A restore from localStorage goes through done() too
       * and must NOT animate -- the customer has seen this case before and is
       * coming back to it, so watching it get drawn again reads as the page
       * being slow. Colour changes and Case Type rebuilds never come here at
       * all; they repaint instantly as they always have.
       */
      if (!state.restored && body.reveal && window.ccReveal) {
        window.ccReveal(body.reveal);
      }
      var started = Date.now();
      var fallbackShown = false;
      state.paintWait = setInterval(function () {
        if (state.status !== 'done') { clearInterval(state.paintWait); state.paintWait = 0; return; }
        if (paintedCanvas()) {
          clearInterval(state.paintWait); state.paintWait = 0;
          removeFallback();                          // one picture, never two
          ready();
        } else if (!fallbackShown && Date.now() - started > 12000) {
          fallbackShown = true;
          if (urls.mockup) showFallbackImage(urls.mockup);
          ready();
        } else if (Date.now() - started > 90000) {
          clearInterval(state.paintWait); state.paintWait = 0;
        }
      }, 120);
    } else if (urls.mockup) {
      showFallbackImage(urls.mockup);
      ready();
    } else {
      fail({ reason: 'no urls' });
    }
  }

  // The case is on screen: progress out, result + thumbnail in, stage on.
  function ready() {
    state.ready = true;
    state.showGallery = false;
    hideProgress();
    syncStage();
    render();
    drawThumb();
    lsSet();
    if (isDesktop()) showDesktopPreviewSlide();
  }

  // Failure leaves the gallery exactly as it is.
  function fail(body) {
    stopPolling();
    state.status = 'failed';
    state.ready = false;
    state.failure = body || {};
    if (window.ccClearArt) window.ccClearArt();
    removeFallback();
    syncStage();
    hideProgress();
    render();
  }

  /* ------------------------------------------------------------- events -- */

  var typeTimer = 0;
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.hasAttribute) return;
    if (t.hasAttribute('data-myoc-link-field')) {
      state.wantPreview = false;
      if (!t.value.trim()) {
        state.linkMatch = null;
        if (state.status !== 'idle') resetAll(); else { lsClear(); render(); }
        syncColourLock();
        return;
      }
      if (looksLikeUrl(t.value)) {
        clearTimeout(linkMatchTimer);
        linkMatchTimer = setTimeout(function () { matchLink(t.value); }, 400);
      }
      syncColourLock();
      clearTimeout(typeTimer);
      typeTimer = setTimeout(render, 200);
      return;
    }
    /*
      * The design note carries no `name` until it says something.
      *
      * The theme posts new FormData(form), and a named-but-empty textarea
      * would put properties[Design Note]="" on every single order. Shopify
      * does drop empty properties, but relying on that puts the promise in
      * someone else's code; taking the name off is the page saying it.
      */
    if (t.hasAttribute('data-myoc-note-field')) {
      writeNote(t.value);           // into the hidden input that is submitted
      saveNote(t.value);            // and remembered if they leave the page
      return;
    }
    if (t.hasAttribute('data-myoc-colour-field')) {
      syncPropertyName(t);                           // named now that it has a colour
      if (e.detail === 'cc') return;                 // the picker wrote it, not a person
      var hex = hexIn(t.value);
      if (hex && window.ccChoose && window.ccHasArt && window.ccHasArt()) {
        window.ccChoose(hex, null, { silent: true });
      }
    }
  });

  // ✕ in the field: empty it, let the "field emptied" path do the rest, and
  // keep the customer where they were.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var c = e.target.closest('[data-myoc-clear]');
    if (!c) return;
    e.preventDefault();
    var f = linkField();
    if (!f) return;
    f.value = '';
    f.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: 'myoc-clear' }));
    try { f.focus({ preventScroll: true }); } catch (err) { f.focus(); }
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var b = e.target.closest('[data-myoc-preview-btn]');
    if (!b) return;
    e.preventDefault();
    if (b.disabled) return;
    var f = linkField();
    if (!f || !looksLikeUrl(f.value)) return;
    run(f.value);
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('[data-myoc-see-photos]')) return;
    e.preventDefault();
    showGallery();
  });

  // Tap the case (or the ⤢) -> the theme's own lightbox with the current
  // render. ccExport() is the configurator's cached PNG, cleared on every
  // repaint, so a colour change is what the lightbox shows; the server's
  // mockup is the fallback when there is no canvas.
  // The gallery's own lightbox list ([data-pzoom-data]), through whatever
  // override is installed (myoc-ride.js narrows it to the selected ride).
  function galleryMedia() {
    var el = document.querySelector('[data-pzoom-data]');
    if (!el) return [];
    var list;
    try { list = JSON.parse(el.textContent); } catch (e) { return []; }
    if (!Array.isArray(list)) return [];
    if (typeof window.pzoomOverride === 'function') {
      try { list = window.pzoomOverride(list.slice()) || list; } catch (e) {}
    }
    return list.filter(function (m) { return m && m.src; });
  }

  function openZoom() {
    if (state.status !== 'done' || !state.ready) return;
    var st = stageEl();
    if (!st || st.hidden) return;
    var src = window.ccExport && window.ccExport();
    if (!src) src = state.result && state.result.urls && state.result.urls.mockup;
    if (!src || !window.pzoomOpen) return;
    var items = [{ id: 'myoc', src: src, full: src, alt: '', caption: strings().preview_headline || '' }];
    // Desktop keeps the old browsing: the preview first, then the product
    // photos behind the arrows. A phone gets the case alone.
    var desktop = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
    if (desktop) items = items.concat(galleryMedia());
    window.pzoomOpen(items, 0, src);
  }
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var st = e.target.closest('[data-myoc-stage]');
    if (!st) return;
    if (e.target.closest('[data-myoc-see-photos]')) return;
    if (!e.target.closest('[data-cc-overlay], [data-myoc-zoom]')) return;
    e.preventDefault();
    openZoom();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var t = e.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-myoc-link-field')) return;
    // Enter in the link box previews instead of submitting the whole form.
    e.preventDefault();
    if (looksLikeUrl(t.value)) run(t.value);
  });

  // The configurator repainted (colour change, repaint after a remount):
  // keep the thumbnail in step.
  document.addEventListener('cc:painted', function () {
    if (state.status === 'done') {
      drawThumb();
      if (state.ready) lsSet();   // the colour changed: remember the new one
      if (slideTimers.length && isDesktop()) showDesktopPreviewSlide();
    }
  });

  // The theme may replace every product block (Case Type change), the media
  // containers, or -- in principle -- the whole column. The stage survives
  // all of it: it is put back if missing and repainted from the plates the
  // configurator still holds. The gallery slides are never touched.
  function remount() {
    var reinserted = ensureStage();
    render();
    syncColourLock();
    syncOptionalProperties();
    if (state.status === 'running' || (state.status === 'done' && !state.ready)) showProgress();
    else if (state.status === 'done' && state.ready) {
      if (reinserted || !paintedCanvas()) {
        var repainted = window.ccRepaint ? window.ccRepaint() : false;   // -> cc:painted -> drawThumb
        if (!repainted && state.result && state.result.urls && state.result.urls.mockup) {
          showFallbackImage(state.result.urls.mockup);
        }
      }
      drawThumb();
      if (isDesktop()) showDesktopPreviewSlideSoon();
    }
  }
  document.addEventListener('tcustomizer-remount', function () { setTimeout(remount, 0); });
  document.addEventListener('shopify:section:load', function () { setTimeout(remount, 0); });

  // Watch the media column (and its parent, in case the column itself is
  // replaced) so a swap that happens without the event is caught too.
  var watched = [];
  function watchMedia() {
    var col = mediaColumn();
    var targets = [];
    if (col) targets.push(col);
    if (col && col.parentNode) targets.push(col.parentNode);
    var dm = desktopMedia();
    if (dm) targets.push(dm);
    for (var i = 0; i < targets.length; i++) {
      var t = targets[i];
      if (watched.indexOf(t) !== -1 || !window.MutationObserver) continue;
      watched.push(t);
      (function (node) {
        var timer = 0;
        new MutationObserver(function () {
          clearTimeout(timer);
          timer = setTimeout(function () { remount(); watchMedia(); }, 50);
        }).observe(node, { childList: true, subtree: node.classList.contains('product-desktop-media') });
      })(t);
    }
  }

  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { syncStage(); if (state.status === 'done' && state.ready) remount(); }, 150);
  });

  function init() {
    ensureStage();
    render();
    syncColourLock();
    primeNote();
    syncOptionalProperties();
    watchMedia();
    restoreLast();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
